// Spielsteuerung: lädt Kapitel/Szenen, verarbeitet Eingaben und führt Regeln/Aktionen aus.
import { Application } from 'pixi.js';
import type { Action, ChapterDef, DialogDef, HotspotDef, ItemDef, Question, PropDef, PuzzleDef, Pt, SceneDef } from '../../shared/types.ts';
import { writeSave, readSave, setSaveChapter, type SlotId } from './save.ts';
import { check, newState, pickRule, type GameState } from '../../shared/logic.ts';
import { SceneView } from '../engine/scene-view.ts';
import { Character, OUTFITS } from '../engine/character.ts';
import { addStrings, has, t } from '../i18n.ts';
import { patch, ui, type ItemView } from './store.ts';
import { painters, ensurePainters } from '../art/index.ts';
import { Rng, makeCanvas } from '../art/paint.ts';
import { get } from 'svelte/store';
import { audio } from '../audio/audio.ts';
import { feature } from '../../shared/features.ts';
import { metaFlags } from '../../shared/meta.ts';
import { haptic } from '../pwa.ts';
import { profile, collect, count, unlockStory } from './profile.ts';

const LONG_PRESS_MS = 450;

interface ChapterBundle {
  chapter: ChapterDef;
  strings: Record<string, unknown>;
  scenes: SceneDef[];
  questions: Question[];
}
const chapterFetches = new Map<string, Promise<ChapterBundle>>();
/** Kapitel laden (früh anstoßbar, parallel zur Renderer-Initialisierung) */
export function fetchChapter(id: string, draft = false): Promise<ChapterBundle> {
  const key = id + (draft ? ':draft' : '');
  let p = chapterFetches.get(key);
  if (!p) {
    p = fetch(draft ? `/api/preview/${id}` : `/api/chapter/${id}`).then((r) => {
      if (!r.ok) throw new Error('Kapitel nicht ladbar: ' + id);
      return r.json();
    });
    chapterFetches.set(key, p);
  }
  return p;
}

export class Game {
  app!: Application;
  view!: SceneView;
  state!: GameState;
  chapter!: ChapterDef;
  scenes = new Map<string, SceneDef>();
  questions = new Map<string, Question>();
  dialogs = new Map<string, DialogDef>();
  items = new Map<string, ItemDef>();
  puzzles = new Map<string, PuzzleDef>();
  busy = false;
  ready = false;
  speechLog: string[] = [];
  private speechSkip: (() => void) | null = null;
  private down: { x: number; y: number; t: number; id: number; type: string; moved: boolean; timer: number } | null = null;
  private iconCache = new Map<string, string>();

  async init(host: HTMLElement) {
    this.app = new Application();
    await this.app.init({
      resizeTo: host,
      background: '#0c1422',
      antialias: false,
      autoDensity: true,
      // erstes Bild in einfacher Auflösung (schneller Start), danach volle Schärfe (sharpen())
      resolution: 1,
      preference: 'webgl',
    });
    host.appendChild(this.app.canvas);
    this.app.canvas.setAttribute('aria-label', t('ui.sceneCanvas'));
    this.app.canvas.style.touchAction = 'none';
    const player = new Character(OUTFITS.mara);
    this.view = new SceneView(this.app, player);
    this.view.hotspotCenters = () =>
      this.activeHotspots().flatMap((h) => {
        const r = this.view.hotspotRect(h);
        return r ? [{ id: h.id, x: r[0] + r[2] / 2, y: r[1] + r[3] / 2 }] : [];
      });
    this.app.ticker.add((tk) => {
      const dt = Math.min(0.1, tk.deltaMS / 1000);
      if (this.view.def) this.view.update(dt);
      if (this.state) this.state.playtimeMs += tk.deltaMS;
      this.updateSpeechPos();
    });
    this.app.renderer.on('resize', () => this.onResize());
    window.addEventListener('resize', () => this.onResize());
    this.bindInput(this.app.canvas);
    window.addEventListener('pagehide', () => this.autosave());
    // Ausbau/Profil geändert → Szene aktualisieren
    profile.subscribe(() => this.view?.def && this.state && this.refresh());
    // Szene pausieren, solange sie vollständig verdeckt ist (Quizshow) – spart Rechenzeit/Akku
    ui.subscribe((s) => {
      if (s.show && this.app.ticker.started) this.app.ticker.stop();
      else if (!s.show && !this.app.ticker.started) this.app.ticker.start();
    });
  }

  /** nach dem ersten Bild auf die Geräteauflösung umschalten */
  sharpen() {
    const r = Math.min(window.devicePixelRatio || 1, 2);
    if (this.app.renderer.resolution === r) return;
    this.app.renderer.resolution = r;
    this.app.resize();
    this.onResize();
  }

  private onResize() {
    if (!this.view.def) return;
    this.view.layout();
    patch({ portrait: this.view.portrait, viewRect: { ...this.view.viewRect } });
  }

  async loadChapter(id: string, draft = false) {
    const b = await fetchChapter(id, draft);
    await ensurePainters([...b.scenes.flatMap((s) => [s.background.painter, ...s.props.map((p) => p.painter)]), ...(b.chapter.items ?? []).map((i) => i.painter)]);
    addStrings(b.strings as any);
    this.chapter = b.chapter;
    for (const s of b.scenes) this.scenes.set(s.id, s);
    for (const it of b.chapter.items ?? []) this.items.set(it.id, it);
    for (const pz of b.chapter.puzzles ?? []) this.puzzles.set(pz.id, pz);
    for (const d of b.chapter.dialogs ?? []) this.dialogs.set(d.id, d);
    for (const q of b.questions) this.questions.set(q.id, q);
  }

  draft = false;
  async start(chapterId: string, saved?: GameState, draft = false) {
    this.draft = draft;
    setSaveChapter(chapterId + (draft ? '-entwurf' : ''));
    patch({ screen: 'loading', ended: null, selected: null });
    this.ready = false;
    performance.mark('chapter-start');
    await this.loadChapter(chapterId, draft);
    performance.measure('chapter-load', 'chapter-start');
    performance.mark('scene-start');
    this.state = saved ?? newState(chapterId, this.chapter.startScene);
    await this.enterScene(this.state.scene, saved ? undefined : this.chapter.startSpawn, saved?.pos);
    performance.measure('scene-build', 'scene-start');
    performance.mark('scene-entered');
    patch({ screen: 'game' });
    this.ready = true;
    this.syncInventory();
  }

  async enterScene(id: string, spawn?: string, pos?: Pt) {
    const def = this.scenes.get(id);
    if (!def) throw new Error('Szene fehlt: ' + id);
    this.state.scene = id;
    this.state.flags['visited_' + id] = true;
    const tod = this.chapter?.timeOfDay;
    if (tod && this.state.flags.time === undefined) this.state.flags.time = tod.start;
    this.view.manualPan = 0;
    this.view.npcFilter = (npc) => this.npcHere(npc, id);
    this.view.load(def, (p) => this.propParams(p), (c) => check(c, this.cs()));
    this.view.setTime(tod ? Number(this.state.flags.time ?? 1) : 1, !!def.outdoor, true);
    this.applyWeather();
    patch({ time: tod ? Number(this.state.flags.time ?? 1) : null });
    audio.ambience(def.ambience ?? 'pub');
    audio.music(def.music ?? 'pub');
    const sp = (spawn && def.spawns[spawn]) || def.spawns.default || Object.values(def.spawns)[0];
    this.view.placePlayer(pos ?? sp, sp?.face);
    performance.mark('scene-loaded');
    patch({ portrait: this.view.portrait, viewRect: { ...this.view.viewRect }, sceneName: t(`${id}.name`), hover: null });
    performance.mark('scene-patched');
    this.autosave();
    const r = pickRule(def.onEnter, this.cs());
    if (r) {
      if (this.paused) this.pendingEnter = () => this.runLocked(r.do, id);
      else await this.run(r.do, id);
    }
  }

  /** Spiel pausiert (Menü offen); Szenen-Intros warten bis resume() */
  paused = true;
  private pendingEnter: (() => Promise<void>) | null = null;
  resume() {
    this.paused = false;
    const p = this.pendingEnter;
    this.pendingEnter = null;
    if (p) p();
  }
  pause() {
    this.paused = true;
  }

  async runLocked(actions: Action[], scene: string) {
    this.busy = true;
    patch({ busy: true });
    try {
      await this.run(actions, scene);
    } finally {
      this.busy = false;
      patch({ busy: false });
    }
  }

  /** Spielzustand inkl. Meta-Flags (Ausbau, Gäste, Sammelobjekte) für Bedingungen */
  cs(): GameState {
    if (!feature.meta) return this.state;
    return { ...this.state, flags: { ...metaFlags(get(profile)), ...this.state.flags } };
  }

  propParams(p: PropDef): Record<string, unknown> | null {
    if (!check(p.showIf, this.cs())) return null;
    const params: Record<string, unknown> = { ...(p.params ?? {}) };
    if (p.stateFrom) {
      const v = this.state.flags[p.stateFrom];
      const cv = v !== undefined ? v : this.cs().flags[p.stateFrom];
      if (cv !== undefined) params.state = cv;
    }
    // Texte auf Objekten (z. B. Kreidetafel) aus der Übersetzung
    if (Array.isArray(params.lines)) params.lines = (params.lines as string[]).map((k) => (has(k) ? t(k) : k));
    if (typeof params.text === 'string' && has(params.text)) params.text = t(params.text);
    for (const [k, v] of Object.entries(params)) {
      if (typeof v === 'string' && v.startsWith('$')) params[k] = this.state.flags[v.slice(1)] ?? 0;
    }
    return params;
  }

  activeHotspots(): HotspotDef[] {
    if (!this.view.def) return [];
    return this.view.def.hotspots.filter((h) => check(h.showIf, this.cs()) && this.view.hotspotRect(h));
  }

  hotspotAt(w: Pt): HotspotDef | undefined {
    // letzte Definition gewinnt (oben liegende Hotspots zuletzt definieren)
    const hs = this.activeHotspots();
    // Figuren stehen vor dem Hintergrund und haben Vorrang
    for (const h of hs) if ('npc' in h.area && this.view.hitTest(h, w)) return h;
    for (let i = hs.length - 1; i >= 0; i--) if (this.view.hitTest(hs[i], w)) return hs[i];
    return undefined;
  }

  hotspotName(h: HotspotDef) {
    return t(`${this.state.scene}.${h.id}.name`);
  }

  // ---------------- Eingabe ----------------

  private bindInput(el: HTMLCanvasElement) {
    el.addEventListener('pointerdown', (e) => {
      if (this.speechSkip) {
        this.speechSkip();
        return;
      }
      const d = { x: e.offsetX, y: e.offsetY, t: performance.now(), id: e.pointerId, type: e.pointerType, moved: false, timer: 0 };
      if (e.pointerType !== 'mouse') {
        d.timer = window.setTimeout(() => {
          if (this.down === d && !d.moved) {
            this.down = null;
            this.activate(d.x, d.y, 'look');
          }
        }, LONG_PRESS_MS);
      }
      this.down = d;
    });
    el.addEventListener('pointermove', (e) => {
      if (this.down && this.down.id === e.pointerId) {
        const dx = e.offsetX - this.down.x;
        if (Math.abs(dx) > 12 || Math.abs(e.offsetY - this.down.y) > 12) this.down.moved = true;
        if (this.down.moved && this.down.type !== 'mouse') {
          this.view.panBy(e.movementX || dx * 0.1);
        }
      }
      if (e.pointerType === 'mouse') this.hover(e.offsetX, e.offsetY);
    });
    el.addEventListener('pointerup', (e) => {
      const d = this.down;
      this.down = null;
      if (!d || d.id !== e.pointerId) return;
      clearTimeout(d.timer);
      if (d.moved) return;
      if (e.button === 2) return;
      this.activate(e.offsetX, e.offsetY, 'use');
    });
    el.addEventListener('pointerleave', () => patch({ hover: null }));
    el.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      this.activate(e.offsetX, e.offsetY, 'look');
    });
  }

  private hover(x: number, y: number) {
    if (!this.ready || !this.view.inView(x, y)) {
      patch({ hover: null });
      return;
    }
    const h = this.hotspotAt(this.view.screenToWorld(x, y));
    patch({ hover: h ? { name: this.hotspotName(h), x, y } : null });
    this.app.canvas.style.cursor = h ? 'pointer' : 'default';
  }

  /** Klick/Tap (use) oder Long-Press/Rechtsklick (look) an Bildschirmposition */
  async activate(x: number, y: number, mode: 'use' | 'look') {
    if (!this.ready || this.busy || get(ui).quiz || get(ui).dialog || get(ui).show) return;
    if (!this.view.inView(x, y)) return;
    const w = this.view.screenToWorld(x, y);
    const h = this.hotspotAt(w);
    if (!h) {
      if (get(ui).selected) patch({ selected: null });
      this.view.walkTo(w);
      return;
    }
    if (mode === 'use') {
      // auf Touch: Namen kurz zeigen
      patch({ hover: { name: this.hotspotName(h), x, y } });
    }
    await this.interact(h, mode);
  }

  async interact(h: HotspotDef, mode: 'use' | 'look', item?: string | null) {
    const sel = item === undefined ? get(ui).selected : item;
    this.busy = true;
    patch({ busy: true });
    try {
      if (h.walkTo) {
        await this.view.walkTo(h.walkTo);
      } else if (mode === 'use' || h.exit) {
        const r = this.view.hotspotRect(h)!;
        await this.view.walkTo({ x: r[0] + r[2] / 2, y: r[1] + r[3] + 20 });
      }
      if (h.face) this.view.player.setFacing(h.face);
      else {
        const r = this.view.hotspotRect(h)!;
        const cx = r[0] + r[2] / 2;
        if (Math.abs(cx - this.view.player.x) > 20) this.view.player.setFacing(cx < this.view.player.x ? 'left' : 'right');
      }
      const scene = this.state.scene;
      if (sel && mode === 'use') {
        patch({ selected: null });
        const acts = h.items?.[sel];
        if (acts) await this.run(acts, scene, h.id);
        else await this.say('ui.cantUse');
        return;
      }
      if (mode === 'look') {
        const r = pickRule(h.look, this.cs());
        if (r) await this.run(r.do, scene, h.id);
        else await this.say(`${scene}.${h.id}.look`);
        return;
      }
      if (h.exit) {
        const r = pickRule(h.use, this.cs());
        if (r) {
          await this.run(r.do, scene, h.id);
          return;
        }
        this.sfx('door');
        await this.fade();
        await this.enterScene(h.exit.scene, h.exit.spawn);
        return;
      }
      const r = pickRule(h.use, this.cs());
      if (r) await this.run(r.do, scene, h.id);
      else {
        const k = `${scene}.${h.id}.use`;
        await this.say(has(k) ? k : `${scene}.${h.id}.look`);
      }
    } finally {
      this.busy = false;
      patch({ busy: false });
      this.autosave();
    }
  }

  // ---------------- Aktionen ----------------

  async run(actions: Action[], scene: string, hotspot?: string) {
    for (const a of actions) {
      if ('say' in a) {
        let key = a.say;
        if (key.startsWith('.')) key = `${scene}.${hotspot}${key}`;
        await this.say(key, a.vars);
      } else if ('set' in a) {
        Object.assign(this.state.flags, a.set);
        this.refresh();
      } else if ('give' in a) {
        if (!this.state.inventory.includes(a.give)) this.state.inventory.push(a.give);
        this.sfx('pickup');
        haptic(25);
        this.syncInventory();
        this.refresh();
      } else if ('take' in a) {
        this.state.inventory = this.state.inventory.filter((i) => i !== a.take);
        this.syncInventory();
        this.refresh();
      } else if ('go' in a) {
        this.sfx('door');
        await this.fade();
        await this.enterScene(a.go, a.spawn);
      } else if ('puzzle' in a) {
        const ok = await this.openPuzzle(a.puzzle);
        if (ok) await this.run(this.puzzles.get(a.puzzle)!.onSolve, scene, hotspot);
      } else if ('wait' in a) {
        await new Promise((r) => setTimeout(r, a.wait));
      } else if ('end' in a) {
        count('chapter_' + a.end);
        patch({ ended: a.end });
      } else if ('collect' in a) {
        collect(a.collect);
        this.refresh();
      } else if ('shop' in a) {
        patch({ shop: true });
      } else if ('time' in a) {
        this.state.flags.time = a.time === 'next' ? (Number(this.state.flags.time ?? 1) + 1) % 4 : a.time;
        this.refresh();
      } else if ('guest' in a) {
        patch({ guest: a.guest });
      } else if ('story' in a) {
        unlockStory(a.story, 40);
        this.refresh();
      } else if ('dialog' in a) {
        await this.runDialog(a.dialog);
      } else if ('show' in a) {
        patch({ show: true });
      } else if ('sfx' in a) {
        this.sfx(a.sfx);
      }
    }
  }

  /** Tagesablauf: Figur mit Routine ist nur in der Szene ihrer Tageszeit */
  npcHere(npc: string, scene = this.state.scene): boolean {
    const r = this.chapter?.routines?.[npc];
    if (!r || !this.chapter?.timeOfDay) return true;
    return r[String(this.state.flags.time ?? 1) as '0'] === scene;
  }

  /** Stadtkarte: Schnellreise zu einer schon besuchten Szene */
  async travel(scene: string) {
    if (this.busy || !this.state.flags['visited_' + scene] || !this.scenes.has(scene)) return false;
    this.busy = true;
    patch({ busy: true, map: false });
    try {
      await this.fade();
      await this.enterScene(scene);
    } finally {
      this.busy = false;
      patch({ busy: false });
    }
    return true;
  }

  /** Zeit vergehen lassen (Karte: „Warten“) */
  passTime() {
    if (!this.chapter?.timeOfDay) return;
    this.state.flags.time = ((Number(this.state.flags.time ?? 1) + 1) % 4);
    this.refresh();
    this.autosave();
  }

  /** Wetter aus Tageszeit (oder Flag „weather“ aus den Kapiteldaten): Morgennebel, mittags/abends klar, nachts Regen */
  weatherNow(): 'clear' | 'rain' | 'fog' | 'storm' {
    const f = this.state.flags.weather;
    if (f === 'clear' || f === 'rain' || f === 'fog' || f === 'storm') return f;
    if (!this.chapter?.timeOfDay) return 'clear';
    return (['fog', 'clear', 'clear', 'rain'] as const)[Number(this.state.flags.time ?? 1)] ?? 'clear';
  }

  private applyWeather() {
    const def = this.view.def;
    const tod = !!this.chapter?.timeOfDay;
    const time = tod ? Number(this.state.flags.time ?? 1) : 1;
    const w = this.weatherNow();
    if (this.view.weather !== w || !tod || (this.view as any).lastWeatherScene !== def.id) {
      (this.view as any).lastWeatherScene = def.id;
      this.view.setWeather(w, !!def.outdoor && tod, def.ambience === 'harbor' && time < 3);
    }
    audio.setMood(tod ? time : def.ambience === 'harbor' ? 3 : 1);
  }

  refresh() {
    if (this.view?.def && this.chapter?.timeOfDay) {
      this.view.setTime(Number(this.state.flags.time ?? 1), !!this.view.def.outdoor);
      this.applyWeather();
      patch({ time: Number(this.state.flags.time ?? 1) });
    }
    this.syncQuests();
    this.view.refreshProps((p) => this.propParams(p));
    this.view.refreshNpcs((c) => check(c, this.cs()));
    this.view.refreshLights((c) => check(c, this.cs()));
  }

  say(key: string, vars?: Record<string, string>): Promise<void> {
    const text = t(key, vars);
    const p = this.speechAnchor();
    patch({ speech: { text, who: 'mara', x: p.x, y: p.y } });
    this.view.player.talking = true;
    const dur = Math.min(9000, 1400 + text.length * 55);
    this.speechLog.push(text);
    const t0 = performance.now();
    return new Promise((res) => {
      let finished = false;
      const done = () => {
        if (finished) return;
        finished = true;
        clearInterval(timer);
        this.speechSkip = null;
        this.view.player.talking = false;
        patch({ speech: null });
        res();
      };
      const timer = setInterval(() => {
        const el = performance.now() - t0;
        if (el >= ((window as any).__dlrFast ? 150 : dur)) done();
      }, 50);
      // erst nach kurzer Zeit überspringbar, damit der auslösende Klick nicht sofort schließt
      setTimeout(() => {
        if (!finished) this.speechSkip = done;
      }, 200);
    });
  }

  private speechAnchor(): Pt {
    const pl = this.view.player;
    return this.view.worldToScreen({ x: pl.x, y: pl.y + pl.headTop * pl.scale.y });
  }

  private updateSpeechPos() {
    const s = get(ui).speech;
    if (!s) return;
    const p = this.speechAnchor();
    if (Math.abs(p.x - s.x) > 0.5 || Math.abs(p.y - s.y) > 0.5) patch({ speech: { ...s, x: p.x, y: p.y } });
  }

  openPuzzle(id: string): Promise<boolean> {
    const pz = this.puzzles.get(id);
    if (!pz) throw new Error('Rätsel fehlt: ' + id);
    // bereits gesehene Fragen nach hinten; danach passend zum Niveau des Spielers (Nr. 16)
    const level = this.playerLevel();
    const qs = pz.questions
      .map((q) => this.questions.get(q)!)
      .filter(Boolean)
      .sort(
        (x, y) =>
          Number(this.state.seenQuestions.includes(x.id)) - Number(this.state.seenQuestions.includes(y.id)) ||
          Math.abs(x.difficulty - level) - Math.abs(y.difficulty - level),
      );
    return this.openQuiz({ puzzle: id, title: t(`puzzles.${id}.title`), need: pz.need, questions: qs, mode: 'lock' });
  }

  /** Spielerniveau 1–5 aus der eigenen Trefferquote (ab 4 Antworten), sonst 2 */
  playerLevel(): number {
    const total = Number(this.state.flags.qz_total ?? 0), right = Number(this.state.flags.qz_right ?? 0);
    if (total < 4) return 2;
    return Math.max(1, Math.min(5, Math.round(1 + (right / total) * 4)));
  }

  noteAnswer(right: boolean) {
    this.state.flags.qz_total = Number(this.state.flags.qz_total ?? 0) + 1;
    if (right) this.state.flags.qz_right = Number(this.state.flags.qz_right ?? 0) + 1;
  }

  /** eine einzelne Frage (z. B. im Dialog); löst mit richtig/falsch auf */
  askQuestion(qid: string, title: string): Promise<boolean> {
    const q = this.questions.get(qid);
    if (!q) throw new Error('Frage fehlt: ' + qid);
    return this.openQuiz({ puzzle: 'dialog', title, need: 1, questions: [q], mode: 'single' });
  }

  private openQuiz(o: { puzzle: string; title: string; need: number; questions: Question[]; mode: 'lock' | 'single' }): Promise<boolean> {
    return new Promise((resolve) => {
      patch({
        quiz: {
          ...o,
          resolve: (ok) => {
            patch({ quiz: null });
            resolve(ok);
          },
        },
      });
    });
  }

  /** Fortschritt eines Rätsels (z. B. Zahlenrad leuchtet) */
  puzzleProgress(id: string, correct: number, qid?: string) {
    this.state.flags[`${id}_progress`] = correct;
    if (qid && !this.state.seenQuestions.includes(qid)) this.state.seenQuestions.push(qid);
    this.refresh();
  }

  private fade(): Promise<void> {
    const el = document.getElementById('fade');
    if (!el) return Promise.resolve();
    el.classList.add('on');
    return new Promise((r) =>
      setTimeout(() => {
        r();
        setTimeout(() => el.classList.remove('on'), 60);
      }, 320),
    );
  }

  // ---------------- Inventar ----------------

  itemIcon(id: string): string {
    const hit = this.iconCache.get(id);
    if (hit) return hit;
    const def = this.items.get(id);
    const c = makeCanvas(96, 96);
    const ctx = c.getContext('2d')!;
    const p = painters[def?.painter ?? 'item'];
    const params = { ...(def?.params ?? {}) };
    const sz = p.size(params);
    ctx.translate(48 - (sz.ax ?? sz.w / 2) + (sz.ax !== undefined ? 0 : 0), 48 - (sz.ay ?? sz.h / 2));
    p.draw(ctx, params, new Rng(id));
    const url = c.toDataURL();
    this.iconCache.set(id, url);
    return url;
  }

  syncQuests() {
    const qs = (this.chapter?.quests ?? [])
      .filter((q) => check(q.showIf, this.cs()))
      .map((q) => ({ id: q.id, text: t(`quests.${q.id}`), done: check(q.doneIf, this.cs()), side: !!q.side }));
    patch({ quests: qs });
  }

  /** aktuelle (erste offene) Aufgabe mit bisher freigeschalteten Hinweisen */
  currentHint(): { quest: string; title: string; hints: string[]; level: number } | null {
    const open = (this.chapter?.quests ?? []).filter((q) => check(q.showIf, this.cs()) && !check(q.doneIf, this.cs()));
    // Hinweise zuerst für die Hauptgeschichte, dann für Nebenaufgaben
    const q = open.find((x) => !x.side) ?? open[0];
    if (!q) return null;
    const level = Number(this.state.flags[`hint_${q.id}`] ?? 0);
    const hints = [0, 1, 2].filter((i) => i < level).map((i) => t(`hints.${q.id}.${i}`));
    return { quest: q.id, title: t(`quests.${q.id}`), hints, level };
  }

  moreHint() {
    const h = this.currentHint();
    if (!h || h.level >= 3) return;
    this.state.flags[`hint_${h.quest}`] = h.level + 1;
    this.autosave();
  }

  syncInventory() {
    this.syncQuests();
    const inv: ItemView[] = this.state.inventory.map((id) => ({ id, name: t(`items.${id}.name`), icon: this.itemIcon(id) }));
    patch({ inventory: inv });
  }

  selectItem(id: string | null) {
    patch({ selected: get(ui).selected === id ? null : id });
  }

  async lookItem(id: string) {
    if (this.busy) return;
    this.busy = true;
    try {
      await this.say(`items.${id}.look`);
    } finally {
      this.busy = false;
    }
  }

  showHotspots() {
    this.view.hintsUntil = performance.now() + 2200;
  }

  // ---------------- Dialoge ----------------

  async runDialog(id: string) {
    const d = this.dialogs.get(id);
    if (!d) throw new Error('Dialog fehlt: ' + id);
    let nodeId: string | undefined = typeof d.start === 'string' ? d.start : d.start.find((r) => check(r.if, this.cs()))?.node;
    const npcName = t(`npcs.${d.npc}.name`);
    const used = (this.state.flags[`dlg_used_${id}`] as string | undefined)?.split(',') ?? [];
    let guard = 0;
    while (nodeId && guard++ < 200) {
      const node = d.nodes[nodeId];
      if (!node) throw new Error(`Dialogknoten fehlt: ${id}.${nodeId}`);
      if (node.do) await this.run(node.do, this.state.scene);
      const lines = node.lines ?? [];
      for (let i = 0; i < lines.length; i++) {
        const who = lines[i];
        await this.dialogLine(who === 'mara' ? t('npcs.mara.name') : npcName, who, t(`dialogs.${id}.${nodeId}.${i}`));
      }
      if (node.quiz) {
        const qs = Array.isArray(node.quiz.question) ? node.quiz.question : [node.quiz.question];
        const qid = qs.find((q) => !this.state.seenQuestions.includes(q)) ?? qs[Math.floor(Math.random() * qs.length)];
        patch({ dialog: null });
        const ok = await this.askQuestion(qid, npcName);
        nodeId = ok ? node.quiz.right : node.quiz.wrong;
        continue;
      }
      if (node.choices?.length) {
        const avail = node.choices.filter((c) => check(c.if, this.cs()) && !(c.once && used.includes(c.id)));
        const choice = await this.dialogChoice(npcName, avail.map((c) => ({ id: c.id, label: t(`dialogs.${id}.c.${c.id}`) })));
        const c = avail.find((x) => x.id === choice)!;
        if (c.once) {
          used.push(c.id);
          this.state.flags[`dlg_used_${id}`] = used.join(',');
        }
        await this.dialogLine(t('npcs.mara.name'), 'mara', t(`dialogs.${id}.c.${c.id}`), true);
        if (c.do) await this.run(c.do, this.state.scene);
        if (c.end) break;
        nodeId = c.next;
        continue;
      }
      if (node.end) break;
      nodeId = node.next;
    }
    patch({ dialog: null });
    this.autosave();
  }

  private dialogResolve: ((v: string) => void) | null = null;

  private dialogLine(name: string, who: string, text: string, echo = false): Promise<void> {
    if (echo && (window as any).__dlrFast) return Promise.resolve();
    this.speechLog.push(text);
    return new Promise((res) => {
      patch({ dialog: { name, who, text, choices: null, echo } });
      this.dialogResolve = () => res();
      if ((window as any).__dlrFast) setTimeout(() => this.dialogAdvance('next'), 60);
    });
  }

  private dialogChoice(name: string, choices: { id: string; label: string }[]): Promise<string> {
    return new Promise((res) => {
      patch({ dialog: { name, who: 'mara', text: '', choices, echo: false } });
      this.dialogResolve = res;
    });
  }

  /** von der Oberfläche: Weiter-Klick oder gewählte Antwort */
  dialogAdvance(v: string) {
    const r = this.dialogResolve;
    this.dialogResolve = null;
    r?.(v);
  }

  // ---------------- Kombinieren ----------------

  async combine(a: string, b: string) {
    if (this.busy || a === b) return;
    patch({ selected: null });
    const acts = this.items.get(a)?.combine?.[b] ?? this.items.get(b)?.combine?.[a];
    this.busy = true;
    patch({ busy: true });
    try {
      if (acts) {
        this.sfx('combine');
        await this.run(acts, this.state.scene);
      } else await this.say('ui.cantCombine');
    } finally {
      this.busy = false;
      patch({ busy: false });
      this.autosave();
    }
  }

  /** Gegenstand per Drag-and-drop auf die Szene ziehen */
  async dropItem(id: string, x: number, y: number) {
    if (!this.ready || this.busy || !this.view.inView(x, y)) return;
    const h = this.hotspotAt(this.view.screenToWorld(x, y));
    if (!h) return;
    await this.interact(h, 'use', id);
  }

  sfx(name: string) {
    audio.sfx(name);
  }

  // ---------------- Speichern ----------------

  private saving: Promise<void> | null = null;
  private saveAgain = false;
  savedAt = 0;
  /** Autosave: sofort schreiben; kommen weitere Änderungen während des Schreibens, folgt ein zweiter Durchgang */
  autosave() {
    if (!this.state || !this.ready) return;
    if (this.saving) {
      this.saveAgain = true;
      return;
    }
    this.saving = this.saveTo('auto').finally(() => {
      this.saving = null;
      this.savedAt = Date.now();
      if (this.saveAgain) {
        this.saveAgain = false;
        this.autosave();
      }
    });
  }

  async saveTo(slot: SlotId) {
    this.state.pos = { x: Math.round(this.view.player.x), y: Math.round(this.view.player.y) };
    this.state.updatedAt = Date.now();
    await writeSave({ slot, state: this.state, savedAt: Date.now(), sceneName: t(`${this.state.scene}.name`) });
  }

  async loadFrom(slot: SlotId): Promise<boolean> {
    const s = await readSave(slot);
    if (!s) return false;
    await this.restore(s.state);
    return true;
  }

  async restore(st: GameState) {
    this.state = JSON.parse(JSON.stringify(st));
    patch({ ended: null, selected: null, quiz: null, dialog: null });
    await this.enterScene(this.state.scene, undefined, this.state.pos);
    this.syncInventory();
  }

  async newGame() {
    await this.restore(newState(this.chapter.id, this.chapter.startScene));
    const sp = this.view.def.spawns[this.chapter.startSpawn ?? 'default'];
    if (sp) this.view.placePlayer(sp, sp.face);
    this.autosave();
  }
}

export const game = new Game();
